import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { ThreadPanel } from "./ThreadPanel.js";
import { clearDraft, getChannelMessages, getDraft, sendMessage, setDraft as persistDraft, } from "@kritaka/core";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Virtuoso } from "react-virtuoso";
import remarkGfm from "remark-gfm";
import { useDataContext } from "../hub/data-context-provider.js";
import { DecisionCompletedCard } from "./DecisionCompletedCard.js";
import { MessageCodeBlock } from "./MessageCodeBlock.js";
import { useRelay } from "../hub/relay-provider.js";
import { HoverTooltip } from "./HoverTooltip.js";
import { AGENT_PILL_BG, AGENT_PILL_COLOR, OWNER_PILL_BG, OWNER_PILL_COLOR, } from "./identity.js";
const DEFAULT_EMOJIS = ["👍", "❤️", "🎉", "👀", "🔥", "✅"];
// Shared empty-reactions reference. `reactions[msg.id] ?? []` would mint a
// fresh array on every render for every message that doesn't have any
// reactions, defeating React.memo's shallow prop comparison on the
// MessageBubble. Threading the same constant through keeps that prop
// referentially stable (KTK-273).
const NO_REACTIONS = [];
// MRU emoji storage — Discord-style "3 most-recently-used" inline (KTK-273).
// Workspace-scoped so different teams don't bleed into each other.
const MRU_LIMIT = 3;
function mruStorageKey(workspaceId) {
    return `kritaka:reaction-mru:${workspaceId}`;
}
function loadReactionMru(workspaceId) {
    try {
        const raw = window.localStorage.getItem(mruStorageKey(workspaceId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter((e) => typeof e === "string" && e.length > 0).slice(0, MRU_LIMIT);
    }
    catch {
        return [];
    }
}
function saveReactionMru(workspaceId, emoji) {
    const current = loadReactionMru(workspaceId);
    const next = [emoji, ...current.filter((e) => e !== emoji)].slice(0, MRU_LIMIT);
    try {
        window.localStorage.setItem(mruStorageKey(workspaceId), JSON.stringify(next));
    }
    catch {
        // localStorage full / disabled — MRU degrades to in-memory only this
        // session, no big deal.
    }
    return next;
}
// Common emoji categories surfaced by the "more" reaction picker. Kept
// short and curated rather than the full Unicode emoji table — KTK-273
// is about removing noise, not adding it. The redesign pass can swap in
// a richer picker later.
const ADDITIONAL_EMOJIS = [
    "👍", "👎", "❤️", "💚", "💙", "💜", "🧡", "💛", "🤍", "🖤",
    "🎉", "🚀", "🔥", "💯", "✨", "⭐", "🌟", "💫", "🙌", "👏",
    "👀", "✅", "❌", "⚠️", "❓", "❗",
    "😂", "😅", "😎", "😢", "😡", "🤔", "🤯", "🥹", "🫡", "🤝",
    "🐛", "📌", "📎", "🛠", "🧪", "📝", "💡", "🤖",
];
const toolbarBtnStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 13,
    color: "var(--text-muted)",
    fontFamily: "inherit",
};
export function ChannelView({ workspaceId, channelId, channelName, channelDescription, authorName, displayName, authorId = null, messagesFooter, onChannelDeleted, openThreadRequest, }) {
    // Default displayName to handle so callers that haven't migrated yet stay
    // correct. Once all surfaces pass it explicitly this fallback can drop.
    const stampName = displayName ?? authorName;
    const ctx = useDataContext();
    const [messages, setMessages] = useState([]);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [input, setInput] = useState("");
    const [pendingImages, setPendingImages] = useState([]);
    const [reactions, setReactions] = useState({});
    const [subscribers, setSubscribers] = useState([]);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [showPinned, setShowPinned] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showToolbar, setShowToolbar] = useState(false);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    // KTK-385 — channel threads. `threads` is keyed by root_message_id for the
    // inline affordance lookup; `openThread` drives the side panel. The
    // thread-title dialog holds the message being threaded until submit.
    const [threads, setThreads] = useState(new Map());
    const [openThread, setOpenThread] = useState(null);
    const [threadDraft, setThreadDraft] = useState(null);
    const [threadDraftTitle, setThreadDraftTitle] = useState("");
    const [threadDraftError, setThreadDraftError] = useState(null);
    // KTK-276 — tracks whether a send is currently in flight. The Send button
    // and the Enter-to-send keypath gate on this so a second click / Enter
    // during the image-upload window can't double-submit. Reset in `finally`
    // so a network failure unwedges the button.
    const [isSending, setIsSending] = useState(false);
    const isSendingRef = useRef(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState("");
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    // Workspace-custom reaction set (admin-editable via WorkspaceSettingsDialog).
    // Falls back to DEFAULT_EMOJIS until the workspace setting resolves / when
    // no custom set is configured.
    const [customEmojis, setCustomEmojis] = useState(null);
    const favoriteEmojis = customEmojis ?? DEFAULT_EMOJIS;
    // Per-user MRU reaction list, persisted in localStorage. Drives the
    // 3-emoji inline strip on each message-hover row (KTK-273).
    const [reactionMru, setReactionMru] = useState(() => loadReactionMru(workspaceId));
    useEffect(() => {
        setReactionMru(loadReactionMru(workspaceId));
    }, [workspaceId]);
    // KTK-273 — message context menu (right-click). Lifted here so the menu
    // overlays the entire channel view; click-outside / Escape dismisses.
    // KTK-319 — `canEdit` rides along so the menu can conditionally show
    // the Edit item; computed by the bubble (it knows isOwn + isHuman) at
    // open time so the menu doesn't have to look the message back up.
    const [contextMenu, setContextMenu] = useState(null);
    // KTK-319 — which message is currently in edit mode. Lifted here so
    // the right-click / three-dot menu's Edit item (which paints at the
    // ChannelView level) can flip the right bubble into edit mode without
    // a separate per-bubble imperative handle.
    const [editingMessageId, setEditingMessageId] = useState(null);
    // KTK-273 — selection toolbar state. Anchor coords are in viewport space.
    const [selectionToolbar, setSelectionToolbar] = useState(null);
    const virtuosoRef = useRef(null);
    const inputRef = useRef(null);
    const draftTimerRef = useRef(null);
    // KTK-320 — stable ref so MessageBubble's React.memo holds across
    // unrelated parent re-renders. The previous shape was an inline
    // arrow at the call site, which created a fresh function ref on
    // every render; that broke memoization for every visible bubble and
    // sent the whole list through React's reconciler on every relay
    // frame, wiping in-progress text selections (the bug KTK-273's
    // Virtuoso `computeItemKey` was supposed to fix at the list level).
    const cancelMessageEdit = useCallback(() => {
        setEditingMessageId(null);
    }, []);
    // Bump MRU when the user reacts. The runtime `setReactionMru` rerender
    // is independent of the network round-trip in `handleReact` below, so
    // the MRU strip updates instantly on click — no flicker waiting for
    // the relay echo.
    const bumpReactionMru = useCallback((emoji) => {
        setReactionMru(saveReactionMru(workspaceId, emoji));
    }, [workspaceId]);
    const openMessageContextMenu = useCallback((messageId, content, senderName, isPinned, canEdit, x, y) => {
        setContextMenu({ messageId, content, senderName, isPinned, canEdit, x, y });
    }, []);
    // Inline reply trigger from the message action strip. Same shape as the
    // selection-toolbar reply (blockquote + sender mention + cursor below)
    // but quotes the entire message instead of a selection range. Stable
    // reference so MessageBubble's React.memo holds.
    const replyToMessage = useCallback((_messageId, content, senderName) => {
        const quoted = content
            .split("\n")
            .map((l) => `> ${l}`)
            .join("\n");
        setInput((prev) => {
            const sep = prev.length > 0 && !prev.endsWith("\n") ? "\n" : "";
            // Blank line between the quote and the @-mention so the two read
            // as distinct paragraphs in the rendered message — without it the
            // `> …` block visually runs into the `@<sender>` line.
            return `${prev}${sep}${quoted}\n\n@${senderName} `;
        });
        window.setTimeout(() => {
            const el = inputRef.current;
            if (!el)
                return;
            el.focus();
            const len = el.value.length;
            el.setSelectionRange(len, len);
            // Resize-on-prefill: the textarea autosizes via `adjustTextareaHeight`
            // on user input, but a programmatic value change doesn't fire input.
            // Recompute height so the prefilled quote + mention is visible
            // without the user having to type a character first.
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
        }, 0);
    }, []);
    // The full emoji-picker set — workspace favorites lead, then ADDITIONAL_EMOJIS
    // for the long tail. Memoized so MessageBubble's prop reference stays stable
    // across re-renders that don't actually change the favorites set.
    const fullEmojiPickerSet = useMemo(() => {
        const out = [];
        for (const e of favoriteEmojis) {
            if (!out.includes(e))
                out.push(e);
        }
        for (const e of ADDITIONAL_EMOJIS) {
            if (!out.includes(e))
                out.push(e);
        }
        return out;
    }, [favoriteEmojis]);
    // KTK-273 — global selection listener for the floating reply/copy/forward
    // toolbar. Any non-empty selection that lives inside a message row
    // anchors the toolbar; selecting in the composer or outside a message
    // dismisses it. The listener has to live at the document level because
    // selectionchange doesn't fire on the element that holds the selection
    // — it fires on `document`.
    useEffect(() => {
        function readSelection() {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
                setSelectionToolbar(null);
                return;
            }
            const range = sel.getRangeAt(0);
            const start = range.startContainer instanceof Element
                ? range.startContainer
                : range.startContainer.parentElement;
            const end = range.endContainer instanceof Element
                ? range.endContainer
                : range.endContainer.parentElement;
            const messageEl = start?.closest("[data-message-id]") ??
                end?.closest("[data-message-id]");
            // Both endpoints must live inside the *same* message — selections
            // that cross message boundaries get a Copy-style action via the
            // global keyboard, not a Reply prefill that wouldn't have a clear
            // sender to mention.
            if (!messageEl ||
                start?.closest("[data-message-id]") !== end?.closest("[data-message-id]")) {
                setSelectionToolbar(null);
                return;
            }
            const messageId = messageEl.getAttribute("data-message-id") ?? "";
            const senderName = messageEl.getAttribute("data-message-author") ?? "";
            const text = sel.toString();
            if (!text.trim()) {
                setSelectionToolbar(null);
                return;
            }
            const rect = range.getBoundingClientRect();
            // Place the toolbar just above the selection's top edge, like the
            // composer's existing toolbar; nudge inward so it doesn't clip on
            // narrow selections at the right edge.
            const toolbarHeight = 28;
            const x = rect.left + rect.width / 2 - 100;
            const y = Math.max(8, rect.top - toolbarHeight - 4);
            setSelectionToolbar({ messageId, senderName, selectedText: text, x, y });
        }
        document.addEventListener("selectionchange", readSelection);
        return () => document.removeEventListener("selectionchange", readSelection);
    }, []);
    // Clear transient overlays when the channel changes; mid-channel state
    // is meaningless on a different channel and would float over wrong
    // content.
    useEffect(() => {
        setContextMenu(null);
        setSelectionToolbar(null);
    }, [channelId]);
    const loadMessages = useCallback(async () => {
        const ms = await getChannelMessages(ctx, workspaceId, channelId, { limit: 100 });
        // kritaka-electron's dedupe — sync can produce duplicates with slightly different
        // ids + near-equal timestamps. Key: author + content + ts-rounded-to-10s.
        // The cloud `listMessages` endpoint already returns ascending (oldest first);
        // Virtuoso renders ascending with `initialTopMostItemIndex={last}` so the
        // newest message sits at the bottom of the viewport. Do NOT reverse here —
        // Electron's channel-store reverses only because SQLite returns DESC, a path
        // web doesn't share.
        const seen = new Set();
        const deduped = ms.filter((m) => {
            const ts = Math.floor(new Date(m.created_at).getTime() / 10000);
            const key = `${m.author_name}:${ts}:${m.content}`;
            if (seen.has(key))
                return false;
            seen.add(key);
            return true;
        });
        setMessages(deduped);
    }, [ctx, workspaceId, channelId]);
    // KTK-385 — threads for the inline affordances. Degrades to empty on a
    // Hub predating the endpoint (the affordance simply doesn't render).
    const loadThreads = useCallback(async () => {
        try {
            const list = await ctx.hub.listThreads(workspaceId, channelId);
            setThreads(new Map(list.map((t) => [t.root_message_id, t])));
        }
        catch (e) {
            console.warn("[ChannelView] listThreads failed; rendering no affordances:", e);
            setThreads(new Map());
        }
    }, [ctx.hub, workspaceId, channelId]);
    const loadReactions = useCallback(async () => {
        try {
            const r = await ctx.hub.getChannelReactions(channelId);
            setReactions(r);
        }
        catch {
            // Silent — channel reactions endpoint may not be wired yet.
        }
    }, [ctx.hub, channelId]);
    const loadPinned = useCallback(async () => {
        try {
            const pinned = await ctx.hub.listPinnedMessages(workspaceId, channelId);
            setPinnedMessages(pinned);
        }
        catch {
            setPinnedMessages([]);
        }
    }, [ctx.hub, workspaceId, channelId]);
    const loadSubscribers = useCallback(async () => {
        try {
            const subs = await ctx.hub.getChannelSubscribers(workspaceId, channelId);
            setSubscribers(subs);
        }
        catch {
            setSubscribers([]);
        }
    }, [ctx.hub, workspaceId, channelId]);
    // Workspace-scoped custom reactions (one fetch per workspace).
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getWorkspaceSetting(workspaceId, "reactionEmojis")
            .then((v) => {
            if (cancelled)
                return;
            if (Array.isArray(v)) {
                const normalized = v.filter((e) => typeof e === "string" && e.length > 0);
                if (normalized.length > 0) {
                    setCustomEmojis(normalized);
                    return;
                }
            }
            setCustomEmojis(null);
        })
            .catch(() => {
            if (!cancelled)
                setCustomEmojis(null);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // Reset state + load everything when the channel changes
    useEffect(() => {
        setMessages([]);
        setHasMoreMessages(true);
        setLoadingOlder(false);
        setPinnedMessages([]);
        setReactions({});
        setSubscribers([]);
        setInput("");
        setPendingImages([]);
        setThreads(new Map());
        setOpenThread(null);
        setThreadDraft(null);
        void loadMessages();
        void loadReactions();
        void loadPinned();
        void loadSubscribers();
        void loadThreads();
    }, [channelId, loadMessages, loadReactions, loadPinned, loadSubscribers, loadThreads]);
    // KTK-397 — sidebar thread navigation lands here as a request.
    useEffect(() => {
        if (openThreadRequest && openThreadRequest.channel_id === channelId) {
            setOpenThread(openThreadRequest);
        }
    }, [openThreadRequest, channelId]);
    // Live event subscription — new messages arrive via relay WSS and append
    // in real time. The relay protocol uses flat frames (author/content/metadata
    // at the top level, matching Electron's channel-hub.sendHumanMessage), not
    // a nested `msg.message` envelope, so we reconstruct Message objects here.
    const relay = useRelay();
    useEffect(() => {
        return relay.onMessage((msg) => {
            if (msg.channel_id !== channelId)
                return;
            if (msg.type === "reaction_event") {
                void loadReactions();
                return;
            }
            if (msg.type === "pin_event" || msg.type === "unpin_event") {
                void loadPinned();
                return;
            }
            if (msg.type === "message_deleted" && typeof msg.message_id === "string") {
                const deletedId = msg.message_id;
                setMessages((ms) => ms.filter((m) => m.id !== deletedId));
                return;
            }
            if (msg.type === "message_updated") {
                // Backend sends the delta `{channel_id, message_id, content,
                // edited_at}` on edit; merge into the existing row instead of
                // replacing (we don't get a full Message envelope back over
                // the wire). KTK-319 — `edited_at` rides along so the
                // `(edited)` indicator paints immediately on other tabs/clients
                // without a refetch.
                if (typeof msg.message_id !== "string")
                    return;
                const updatedId = msg.message_id;
                const nextContent = typeof msg.content === "string" ? msg.content : undefined;
                const nextEditedAt = typeof msg.edited_at === "string" ? msg.edited_at : undefined;
                const nestedMessage = msg.message;
                setMessages((ms) => ms.map((m) => {
                    if (m.id !== updatedId)
                        return m;
                    if (nestedMessage)
                        return nestedMessage;
                    const patch = {};
                    if (nextContent !== undefined)
                        patch.content = nextContent;
                    if (nextEditedAt !== undefined)
                        patch.edited_at = nextEditedAt;
                    return Object.keys(patch).length > 0 ? { ...m, ...patch } : m;
                }));
                return;
            }
            // KTK-385/397 — thread lifecycle frames refresh the affordance map
            // (creation, resolve/unresolve) without a full page refetch.
            if (msg.type === "thread_created" || msg.type === "thread_updated") {
                void loadThreads();
                return;
            }
            if (msg.type === "channel_message") {
                // KTK-385 — thread replies stay out of the parent feed (Discord
                // semantics; the Hub's list endpoint excludes them too). They bump
                // the affordance's count/recency instead; the open ThreadPanel has
                // its own listener for the message body.
                if (typeof msg.thread_id === "string" && msg.thread_id.length > 0) {
                    const threadId = msg.thread_id;
                    const ts = typeof msg.timestamp === "string" ? msg.timestamp : new Date().toISOString();
                    setThreads((prev) => {
                        const next = new Map(prev);
                        for (const [rootId, t] of next) {
                            if (t.id === threadId) {
                                next.set(rootId, { ...t, reply_count: t.reply_count + 1, last_reply_at: ts });
                                break;
                            }
                        }
                        return next;
                    });
                    return;
                }
                const candidate = relayFrameToMessage(msg);
                if (!candidate)
                    return;
                setMessages((ms) => {
                    if (ms.some((m) => m.id === candidate.id))
                        return ms;
                    return [...ms, candidate];
                });
            }
        });
    }, [relay, channelId, loadReactions, loadPinned, loadThreads]);
    // Draft rehydrate / persist
    useEffect(() => {
        let cancelled = false;
        getDraft(ctx, workspaceId, channelId).then((d) => {
            if (!cancelled)
                setInput(d);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId, channelId]);
    useEffect(() => {
        if (draftTimerRef.current)
            clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(() => {
            void persistDraft(ctx, workspaceId, channelId, input);
        }, 250);
        return () => {
            if (draftTimerRef.current)
                clearTimeout(draftTimerRef.current);
        };
    }, [ctx, workspaceId, channelId, input]);
    const loadOlder = useCallback(async () => {
        const oldest = messages[0];
        if (!oldest)
            return false;
        const older = await getChannelMessages(ctx, workspaceId, channelId, {
            limit: 100,
            before: oldest.created_at,
        });
        if (older.length === 0)
            return false;
        // `older` is already ascending (server reverses DESC→ASC). Just filter out
        // anything we already have and prepend — prepending ascending older pages
        // to the front of the ascending current list keeps overall order intact.
        const existingIds = new Set(messages.map((m) => m.id));
        const novel = older.filter((m) => !existingIds.has(m.id));
        if (novel.length === 0)
            return older.length >= 100;
        setMessages((ms) => [...novel, ...ms]);
        return true;
    }, [ctx, workspaceId, channelId, messages]);
    const adjustTextareaHeight = () => {
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
        }
    };
    const insertFormatting = useCallback((prefix, suffix, placeholder) => {
        const el = inputRef.current;
        if (!el)
            return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const selected = input.slice(start, end);
        const text = selected || placeholder || "";
        const newVal = input.slice(0, start) + prefix + text + suffix + input.slice(end);
        setInput(newVal);
        setTimeout(() => {
            el.focus();
            const cursorPos = selected
                ? start + prefix.length + text.length + suffix.length
                : start + prefix.length;
            el.setSelectionRange(cursorPos, cursorPos);
        }, 0);
    }, [input]);
    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items)
            return;
        for (const item of Array.from(items)) {
            if (item.type.startsWith("image/")) {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file)
                    continue;
                const reader = new FileReader();
                reader.onload = () => {
                    setPendingImages((prev) => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            }
        }
    };
    const handleSend = async () => {
        // KTK-276 — guard against double-submit. The image-upload step below is
        // an `await` and a re-press of Enter / second Send click during that
        // window would otherwise fire a second `handleSend` that races the
        // first. Ref-checked synchronously so the second call bails before
        // it captures `input` / `pendingImages` for itself.
        if (isSendingRef.current)
            return;
        const text = input.trim();
        if (!text && pendingImages.length === 0)
            return;
        isSendingRef.current = true;
        setIsSending(true);
        try {
            let metadata;
            if (pendingImages.length > 0) {
                const urls = [];
                for (const img of pendingImages) {
                    const match = img.match(/^data:(image\/\w+);base64,(.+)$/);
                    if (match) {
                        try {
                            const uploaded = await ctx.hub.uploadImage(workspaceId, match[2] ?? "", match[1] ?? "image/png");
                            urls.push(uploaded.url);
                        }
                        catch {
                            urls.push(img); // fall back to base64 inline
                        }
                    }
                    else {
                        urls.push(img);
                    }
                }
                metadata = urls.length === 1 ? { image: urls[0] } : { images: urls };
            }
            setInput("");
            setPendingImages([]);
            if (inputRef.current)
                inputRef.current.style.height = "auto";
            if (draftTimerRef.current)
                clearTimeout(draftTimerRef.current);
            await clearDraft(ctx, workspaceId, channelId);
            const sent = await sendMessage(ctx, workspaceId, channelId, {
                content: text || "(image)",
                authorName: stampName,
                metadata,
            });
            // Dedup-by-id: the Hub broadcasts the canonical message via WSS the
            // moment POST is received, so the relay handler at line 245 may have
            // already inserted this message before our await resolves. Without this
            // guard, both inserts land — surfacing as a visible duplicate (one row
            // from the relay path, another from this path). Same-id entry already
            // present means the relay won the race; nothing more to do here.
            setMessages((ms) => {
                if (ms.some((m) => m.id === sent.id))
                    return ms;
                return [...ms, sent];
            });
            // Fan out to other connected clients. Electron's channel-hub does this
            // via sendToRelay(); without it, web→desktop/web→web never see new
            // messages because the cloud POST endpoint only persists to D1.
            relay.send({
                type: "channel_message",
                channel_id: channelId,
                message_id: sent.id,
                content: sent.content,
                author_type: sent.author_type,
                author_id: sent.author_id,
                author_name: sent.author_name,
                metadata: sent.metadata,
                timestamp: sent.created_at,
            });
        }
        finally {
            isSendingRef.current = false;
            setIsSending(false);
        }
    };
    // Latest-reactions ref — the handler reads from this instead of closing
    // over `reactions` directly so its identity stays stable across re-renders
    // (KTK-273 — required for `React.memo` on `MessageBubble` to actually take).
    const reactionsRef = useRef(reactions);
    useEffect(() => {
        reactionsRef.current = reactions;
    });
    const handleReact = useCallback(async (messageId, emoji) => {
        const existing = reactionsRef.current[messageId]?.find((r) => r.emoji === emoji);
        const isRemove = existing?.authors.includes(stampName) ?? false;
        if (isRemove) {
            await ctx.hub.removeReaction(messageId, emoji, stampName, channelId);
        }
        else {
            await ctx.hub.addReaction(messageId, emoji, stampName, channelId, authorId);
            // Bump MRU on add (not remove) — clicking-to-undo shouldn't re-promote.
            bumpReactionMru(emoji);
        }
        // Backend doesn't fan out reaction_event on REST writes (unlike pins).
        // Electron's channel-hub pushes reaction_event via sendToRelay; match
        // that so web → any other client sees the delta without a refetch.
        // Passing a real author_id matters: Desktop's relay handler re-POSTs
        // the reaction to D1, and UNIQUE(message_id, emoji, author_id) only
        // collapses rows when the ids actually match (NULLs don't in SQLite).
        relay.send({
            type: "reaction_event",
            channel_id: channelId,
            message_id: messageId,
            emoji,
            action: isRemove ? "remove" : "add",
            author_type: "human",
            author_id: authorId,
            author_name: stampName,
            timestamp: new Date().toISOString(),
        });
        void loadReactions();
    }, [ctx.hub, channelId, stampName, authorId, loadReactions, relay, bumpReactionMru]);
    const handlePin = useCallback(async (messageId, isPinned) => {
        if (isPinned)
            await ctx.hub.unpinMessage(workspaceId, channelId, messageId);
        else
            await ctx.hub.pinMessage(workspaceId, channelId, messageId);
        void loadPinned();
    }, [ctx.hub, workspaceId, channelId, loadPinned]);
    const handleDeleteMessage = useCallback(async (messageId) => {
        if (!window.confirm("Delete this message? This cannot be undone."))
            return;
        await ctx.hub.deleteMessage(workspaceId, channelId, messageId);
        setMessages((ms) => ms.filter((m) => m.id !== messageId));
    }, [ctx.hub, workspaceId, channelId]);
    const handleEditMessage = useCallback(async (messageId, newContent) => {
        const updated = await ctx.hub.updateMessage(workspaceId, channelId, messageId, newContent);
        setMessages((ms) => ms.map((m) => (m.id === messageId ? updated : m)));
        setEditingMessageId((id) => (id === messageId ? null : id));
    }, [ctx.hub, workspaceId, channelId]);
    const handleDeleteChannel = useCallback(async () => {
        if (!window.confirm(`Delete #${channelName}? This cannot be undone.`))
            return;
        await ctx.hub.deleteChannel(workspaceId, channelId);
        onChannelDeleted?.(channelId);
    }, [ctx.hub, workspaceId, channelId, channelName, onChannelDeleted]);
    const mentionNames = useMemo(() => [authorName, ...subscribers.map((s) => s.name)], [authorName, subscribers]);
    const mentionCandidates = useMemo(() => {
        if (mentionQuery === null)
            return [];
        const q = mentionQuery.toLowerCase();
        return mentionNames
            .map((name, i) => ({ name, id: i === 0 ? "self" : subscribers[i - 1]?.id ?? name }))
            .filter((c) => c.name.toLowerCase().includes(q));
    }, [mentionQuery, mentionNames, subscribers]);
    const insertMention = useCallback((name) => {
        const el = inputRef.current;
        const cursorPos = el?.selectionStart ?? input.length;
        const textBeforeCursor = input.slice(0, cursorPos);
        const atPos = textBeforeCursor.lastIndexOf("@");
        if (atPos === -1)
            return;
        const before = input.slice(0, atPos);
        const after = input.slice(cursorPos);
        const newInput = `${before}@${name} ${after}`;
        setInput(newInput);
        setMentionQuery(null);
        setTimeout(() => {
            const newPos = atPos + name.length + 2;
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(newPos, newPos);
        }, 0);
    }, [input]);
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInput(val);
        adjustTextareaHeight();
        // Detect @mention (skip if inside backticks)
        const cursorPos = e.target.selectionStart ?? val.length;
        const textBeforeCursor = val.slice(0, cursorPos);
        const backtickCount = (textBeforeCursor.match(/`/g) ?? []).length;
        const inBackticks = backtickCount % 2 === 1;
        if (!inBackticks) {
            const atMatch = textBeforeCursor.match(/@(\w*)$/);
            if (atMatch) {
                setMentionQuery(atMatch[1] ?? "");
                setMentionIndex(0);
            }
            else {
                setMentionQuery(null);
            }
        }
        else {
            setMentionQuery(null);
        }
    };
    const handleKeyDown = (e) => {
        if (mentionQuery !== null && mentionCandidates.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIndex((i) => Math.min(i + 1, mentionCandidates.length - 1));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIndex((i) => Math.max(i - 1, 0));
                return;
            }
            if (e.key === "Tab" || e.key === "Enter") {
                e.preventDefault();
                const pick = mentionCandidates[mentionIndex];
                if (pick)
                    insertMention(pick.name);
                return;
            }
            if (e.key === "Escape") {
                setMentionQuery(null);
                return;
            }
        }
        if (e.key === "Enter" && !e.shiftKey && mentionQuery === null) {
            e.preventDefault();
            // KTK-276 — guard at the keypath too. The handler also bails on
            // `isSendingRef`, but stopping here keeps preventDefault from
            // burning a keystroke the user might've meant for a not-yet-rendered
            // composer (e.g. retry-after-error after we re-enable).
            if (isSendingRef.current)
                return;
            void handleSend();
        }
    };
    const pinnedIds = useMemo(() => new Set(pinnedMessages.map((m) => m.id)), [pinnedMessages]);
    // KTK-385 — root message for the open thread's panel header (already in
    // the loaded feed; null if it scrolled out of the fetched window).
    const openThreadRoot = openThread
        ? messages.find((m) => m.id === openThread.root_message_id) ?? null
        : null;
    return (_jsxs("div", { style: { display: "flex", flexDirection: "row", height: "100%", minWidth: 0 }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%", flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            padding: "12px 24px",
                            borderBottom: "1px solid var(--border)",
                            flexShrink: 0,
                            gap: 12,
                        }, children: [_jsxs("span", { style: { fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }, children: ["# ", channelName] }), editingDesc ? (_jsx("input", { type: "text", value: descDraft, onChange: (e) => setDescDraft(e.target.value), onKeyDown: async (e) => {
                                    if (e.key === "Enter") {
                                        await ctx.hub.updateChannelDescription(workspaceId, channelId, descDraft.trim());
                                        setEditingDesc(false);
                                    }
                                    if (e.key === "Escape")
                                        setEditingDesc(false);
                                }, autoFocus: true, placeholder: "Add a description...", style: {
                                    fontSize: 12,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                    width: 250,
                                    outline: "none",
                                    borderLeft: "1px solid var(--border)",
                                    paddingLeft: 12,
                                    marginLeft: 0,
                                    backgroundColor: "var(--bg-tertiary)",
                                    border: "1px solid var(--border)",
                                    color: "var(--text-primary)",
                                } })) : (_jsx("span", { onClick: () => {
                                    setDescDraft(channelDescription ?? "");
                                    setEditingDesc(true);
                                }, style: {
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                    borderLeft: "1px solid var(--border)",
                                    paddingLeft: 12,
                                    cursor: "pointer",
                                }, title: "Click to edit description", children: channelDescription || "Add description..." })), _jsxs("div", { style: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }, children: [pinnedMessages.length > 0 && (_jsxs("button", { type: "button", onClick: () => setShowPinned(!showPinned), style: {
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            fontSize: 12,
                                            color: showPinned ? "var(--accent)" : "var(--text-muted)",
                                        }, children: ["\uD83D\uDCCC ", pinnedMessages.length] })), _jsx(HoverTooltip, { content: _jsx(ChannelRoster, { ownerName: authorName, agents: subscribers }), align: "right", children: _jsxs("span", { style: {
                                                fontSize: 11,
                                                color: "var(--text-muted)",
                                                cursor: "default",
                                                borderBottom: "1px dotted var(--text-muted)",
                                            }, children: [subscribers.length, " agent", subscribers.length !== 1 ? "s" : ""] }) }), _jsx("button", { type: "button", onClick: handleDeleteChannel, title: "Delete channel", style: {
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            fontSize: 14,
                                            color: "var(--text-muted)",
                                            padding: 0,
                                        }, children: "\uD83D\uDDD1" })] })] }), showPinned && pinnedMessages.length > 0 && (_jsxs("div", { style: {
                            padding: "8px 24px",
                            borderBottom: "1px solid var(--border)",
                            backgroundColor: "var(--bg-secondary)",
                            maxHeight: 200,
                            overflowY: "auto",
                            flexShrink: 0,
                        }, children: [_jsx("div", { style: { fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }, children: "PINNED MESSAGES" }), pinnedMessages.map((msg) => (_jsxs("div", { style: { fontSize: 12, padding: "4px 0", color: "var(--text-primary)" }, children: [_jsx("span", { style: {
                                            fontWeight: 600,
                                            color: msg.author_type === "human" ? "var(--accent)" : "var(--success)",
                                        }, children: msg.author_name }), ": ", msg.content.length > 120 ? msg.content.slice(0, 120) + "..." : msg.content] }, msg.id)))] })), _jsxs("div", { style: { flex: 1, minHeight: 0, position: "relative" }, children: [messages.length === 0 ? (_jsxs("div", { style: {
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    gap: 12,
                                }, children: [_jsx("p", { style: { fontSize: 13, color: "var(--text-muted)" }, children: "No messages yet. Say something!" }), messagesFooter && (_jsx("div", { style: { alignSelf: "stretch", padding: "0 24px" }, children: messagesFooter }))] })) : (_jsx(Virtuoso, { ref: virtuosoRef, data: messages, 
                                // KTK-273 — key by message id, not index. Without this, a
                                // shifting messages array (a new message arriving, an edit
                                // landing, etc.) reconciles "item at index N" with whatever
                                // message now sits at index N, repurposing DOM nodes mid-flight.
                                // The browser's Selection range loses its anchor when its
                                // text nodes get swapped underneath, which is the in-progress
                                // copy-selection wipe the channel had been suffering.
                                computeItemKey: (_index, msg) => msg.id, initialTopMostItemIndex: messages.length - 1, followOutput: "auto", overscan: 800, alignToBottom: true, atTopStateChange: (atTop) => {
                                    if (atTop && hasMoreMessages && !loadingOlder) {
                                        setLoadingOlder(true);
                                        loadOlder().then((loaded) => {
                                            if (!loaded)
                                                setHasMoreMessages(false);
                                            setLoadingOlder(false);
                                        });
                                    }
                                }, atBottomStateChange: (atBottom) => setShowJumpToBottom(!atBottom), style: { height: "100%", overflowX: "hidden" }, components: {
                                    Header: () => loadingOlder ? (_jsx("div", { style: { display: "flex", justifyContent: "center", padding: "12px 0" }, children: _jsx("span", { style: { fontSize: 12, color: "var(--text-muted)" }, children: "Loading older messages..." }) })) : null,
                                    Footer: () => messagesFooter ? (_jsx("div", { style: { padding: "0 24px" }, children: messagesFooter })) : null,
                                }, itemContent: (index, msg) => {
                                    // KTK-308 — Discord-style date dividers. Prepend a divider
                                    // above the first message of each calendar day (in the
                                    // viewer's local timezone). Index 0 always gets one so the
                                    // user sees an explicit "where am I in time" anchor at the
                                    // top of the scroll, even before they pull older history.
                                    const prev = index > 0 ? messages[index - 1] : null;
                                    const dayKey = localDayKey(msg.created_at);
                                    const prevKey = prev ? localDayKey(prev.created_at) : null;
                                    const showDivider = prevKey !== dayKey;
                                    return (_jsxs("div", { children: [showDivider && (_jsx(DateDivider, { iso: msg.created_at })), _jsxs("div", { style: { padding: "0 24px" }, children: [_jsx(MessageBubble, { message: msg, reactions: reactions[msg.id] ?? NO_REACTIONS, onReact: handleReact, onPin: handlePin, onDelete: handleDeleteMessage, onEdit: handleEditMessage, onReply: replyToMessage, onContextMenu: openMessageContextMenu, isPinned: pinnedIds.has(msg.id), isEditing: editingMessageId === msg.id, onCancelEdit: cancelMessageEdit, mentionNames: mentionNames, favoriteEmojis: favoriteEmojis, mruEmojis: reactionMru, fullEmojiPickerSet: fullEmojiPickerSet, selfName: stampName, ownerHandle: authorName }), threads.has(msg.id) && (_jsx(ThreadAffordance, { thread: threads.get(msg.id), onOpen: () => setOpenThread(threads.get(msg.id) ?? null) }))] })] }));
                                } })), showJumpToBottom && (_jsx("button", { type: "button", onClick: () => virtuosoRef.current?.scrollToIndex({
                                    index: messages.length - 1,
                                    behavior: "auto",
                                }), style: {
                                    position: "absolute",
                                    bottom: 16,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "6px 16px",
                                    borderRadius: 20,
                                    border: "1px solid var(--border)",
                                    backgroundColor: "var(--bg-secondary)",
                                    color: "var(--text-primary)",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                    zIndex: 10,
                                }, children: "\u2193 Jump to latest" }))] }), _jsxs("div", { style: {
                            flexShrink: 0,
                            padding: "12px 24px 16px",
                            borderTop: "1px solid var(--border)",
                            position: "relative",
                        }, children: [mentionQuery !== null && mentionCandidates.length > 0 && (_jsx("div", { style: {
                                    position: "absolute",
                                    bottom: "100%",
                                    left: 24,
                                    right: 24,
                                    marginBottom: 4,
                                    backgroundColor: "var(--bg-secondary)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 8,
                                    padding: "4px 0",
                                    boxShadow: "0 -4px 16px rgba(0,0,0,0.3)",
                                    maxHeight: 200,
                                    overflowY: "auto",
                                    zIndex: 20,
                                }, children: mentionCandidates.map((c, i) => (_jsxs("div", { onClick: () => insertMention(c.name), onMouseEnter: () => setMentionIndex(i), style: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "8px 14px",
                                        cursor: "pointer",
                                        backgroundColor: i === mentionIndex ? "var(--bg-hover)" : "transparent",
                                    }, children: [_jsx("div", { style: {
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                flexShrink: 0,
                                                backgroundColor: i === 0 ? "var(--accent)" : "var(--success)",
                                                color: "white",
                                            }, children: c.name.charAt(0).toUpperCase() }), _jsxs("span", { style: { fontSize: 13, color: "var(--text-primary)" }, children: ["@", c.name] }), i === 0 && (_jsx("span", { style: { fontSize: 11, color: "var(--text-muted)" }, children: "(you)" }))] }, c.id))) })), pendingImages.length > 0 && (_jsx("div", { style: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }, children: pendingImages.map((img, i) => (_jsxs("div", { style: {
                                        padding: 8,
                                        borderRadius: 8,
                                        backgroundColor: "var(--bg-tertiary)",
                                        border: "1px solid var(--border)",
                                        position: "relative",
                                    }, children: [_jsx("img", { src: img, alt: `Pasted image ${i + 1}`, style: { maxWidth: 150, maxHeight: 100, borderRadius: 4, display: "block" } }), _jsx("button", { type: "button", onClick: () => setPendingImages((prev) => prev.filter((_, j) => j !== i)), style: {
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                border: "none",
                                                cursor: "pointer",
                                                backgroundColor: "rgba(0,0,0,0.6)",
                                                color: "white",
                                                fontSize: 12,
                                            }, children: "x" })] }, i))) })), showPreview && input && (_jsx("div", { style: {
                                    padding: "8px 12px",
                                    marginBottom: 4,
                                    borderRadius: 6,
                                    backgroundColor: "var(--bg-tertiary)",
                                    border: "1px solid var(--border)",
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    color: "var(--text-primary)",
                                    maxHeight: 200,
                                    overflowY: "auto",
                                }, children: _jsx(MarkdownContent, { content: input }) })), _jsxs("div", { style: {
                                    borderRadius: 8,
                                    backgroundColor: "var(--bg-tertiary)",
                                    border: "1px solid var(--border)",
                                    position: "relative",
                                }, children: [showToolbar && (_jsxs("div", { style: {
                                            position: "absolute",
                                            bottom: "100%",
                                            left: 8,
                                            marginBottom: -2,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            padding: "1px 6px",
                                            borderRadius: 8,
                                            backgroundColor: "var(--bg-secondary)",
                                            border: "1px solid var(--border)",
                                            boxShadow: "0 2px 16px rgba(0,0,0,0.4)",
                                            zIndex: 50,
                                        }, children: [_jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    insertFormatting("**", "**", "bold");
                                                }, style: toolbarBtnStyle, title: "Bold", children: _jsx("b", { children: "B" }) }), _jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    insertFormatting("*", "*", "italic");
                                                }, style: toolbarBtnStyle, title: "Italic", children: _jsx("i", { children: "I" }) }), _jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    insertFormatting("`", "`", "code");
                                                }, style: toolbarBtnStyle, title: "Inline code", children: _jsx("code", { style: { fontSize: 11 }, children: "</>" }) }), _jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    insertFormatting("\n```\n", "\n```\n", "code block");
                                                }, style: toolbarBtnStyle, title: "Code block", children: "{ }" }), _jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    insertFormatting("[", "](url)", "link text");
                                                }, style: toolbarBtnStyle, title: "Link", children: "\uD83D\uDD17" }), _jsx("button", { onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    setShowPreview(!showPreview);
                                                }, style: { ...toolbarBtnStyle, color: showPreview ? "var(--accent)" : "var(--text-muted)" }, title: "Toggle preview", children: showPreview ? "✏️" : "👁️" })] })), _jsxs("div", { style: { display: "flex", alignItems: "flex-end" }, children: [_jsx("textarea", { ref: inputRef, value: input, onChange: handleInputChange, onKeyDown: handleKeyDown, onPaste: handlePaste, onSelect: (e) => {
                                                    // Only surface the toolbar when the user has selected text
                                                    // to format — plain focus shouldn't summon it.
                                                    const el = e.currentTarget;
                                                    setShowToolbar(el.selectionStart !== el.selectionEnd);
                                                }, onBlur: () => setTimeout(() => setShowToolbar(false), 200), placeholder: `Message #${channelName} — @ to mention`, rows: 1, style: {
                                                    flex: 1,
                                                    padding: "12px 16px",
                                                    fontSize: 13,
                                                    outline: "none",
                                                    backgroundColor: "transparent",
                                                    border: "none",
                                                    color: "var(--text-primary)",
                                                    resize: "none",
                                                    lineHeight: 1.5,
                                                    fontFamily: "inherit",
                                                    maxHeight: 200,
                                                    overflowY: "auto",
                                                } }), _jsx("button", { type: "button", onClick: handleSend, 
                                                // KTK-276 — disable while a send is in flight so a re-click
                                                // during the image-upload window can't fire a second send.
                                                // `disabled` also greys the button per the existing styling
                                                // when there's no payload to send.
                                                disabled: isSending || (!input.trim() && pendingImages.length === 0), style: {
                                                    padding: "12px 20px",
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    backgroundColor: isSending
                                                        ? "var(--bg-tertiary)"
                                                        : input.trim() || pendingImages.length > 0
                                                            ? "var(--accent)"
                                                            : "transparent",
                                                    color: isSending
                                                        ? "var(--text-muted)"
                                                        : input.trim() || pendingImages.length > 0
                                                            ? "white"
                                                            : "var(--text-muted)",
                                                    border: "none",
                                                    cursor: isSending
                                                        ? "wait"
                                                        : input.trim() || pendingImages.length > 0
                                                            ? "pointer"
                                                            : "default",
                                                    alignSelf: "flex-end",
                                                    opacity: isSending ? 0.7 : 1,
                                                    transition: "opacity 80ms ease-out",
                                                }, children: isSending ? "Sending…" : "Send" })] })] })] }), contextMenu && (_jsx(MessageContextMenu, { x: contextMenu.x, y: contextMenu.y, isPinned: contextMenu.isPinned, canEdit: contextMenu.canEdit, onReply: () => {
                            replyToMessage(contextMenu.messageId, contextMenu.content, contextMenu.senderName);
                            setContextMenu(null);
                        }, onCopy: () => {
                            navigator.clipboard?.writeText(contextMenu.content).catch(() => undefined);
                            setContextMenu(null);
                        }, onPin: () => {
                            void handlePin(contextMenu.messageId, contextMenu.isPinned);
                            setContextMenu(null);
                        }, onEdit: () => {
                            setEditingMessageId(contextMenu.messageId);
                            setContextMenu(null);
                        }, onCreateThread: threads.has(contextMenu.messageId)
                            ? null
                            : () => {
                                setThreadDraft({ messageId: contextMenu.messageId });
                                setThreadDraftTitle("");
                                setThreadDraftError(null);
                                setContextMenu(null);
                            }, onDismiss: () => setContextMenu(null) })), selectionToolbar && (_jsx(MessageSelectionToolbar, { x: selectionToolbar.x, y: selectionToolbar.y, onCopy: () => {
                            navigator.clipboard?.writeText(selectionToolbar.selectedText).catch(() => undefined);
                            setSelectionToolbar(null);
                        }, onForward: () => {
                            // Forwarding lands the quoted text into the composer with no
                            // sender attribution — same as a manual paste, no auto-reply
                            // shape. We can grow this into a workspace/channel picker
                            // later when there's a real cross-channel use case.
                            setInput((prev) => {
                                const quoted = selectionToolbar.selectedText
                                    .split("\n")
                                    .map((l) => `> ${l}`)
                                    .join("\n");
                                const sep = prev.length > 0 && !prev.endsWith("\n") ? "\n" : "";
                                return `${prev}${sep}${quoted}\n`;
                            });
                            setSelectionToolbar(null);
                            window.setTimeout(() => inputRef.current?.focus(), 0);
                        }, onReply: () => {
                            // Discord-style quote-reply: blockquote the selection, mention
                            // the original sender on the next line, leave the cursor a
                            // line below ready to type. The 99% use-case per the spec.
                            //
                            // Snapshot the selection state into locals BEFORE setSelection-
                            // Toolbar(null) — without that snapshot, async state-update
                            // ordering inside the same React batch can leave `selectedText`
                            // stale by the time the setInput updater callback runs and the
                            // composer ends up with only the @-mention.
                            const snapText = selectionToolbar.selectedText;
                            const handle = selectionToolbar.senderName;
                            const quoted = snapText
                                .split("\n")
                                .map((l) => `> ${l}`)
                                .join("\n");
                            setInput((prev) => {
                                const sep = prev.length > 0 && !prev.endsWith("\n") ? "\n" : "";
                                // Blank line between the quote and the @-mention so the two
                                // read as distinct paragraphs in the rendered message —
                                // without it the `> …` block visually runs into the
                                // `@<sender>` line.
                                return `${prev}${sep}${quoted}\n\n@${handle} `;
                            });
                            setSelectionToolbar(null);
                            window.setTimeout(() => {
                                const el = inputRef.current;
                                if (!el)
                                    return;
                                el.focus();
                                const len = el.value.length;
                                el.setSelectionRange(len, len);
                                // Resize-on-prefill — see replyToMessage for the same rationale.
                                el.style.height = "auto";
                                el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
                            }, 0);
                        } })), threadDraft && (_jsx(ThreadTitleDialog, { title: threadDraftTitle, error: threadDraftError, onTitleChange: setThreadDraftTitle, onCancel: () => setThreadDraft(null), onSubmit: () => {
                            const title = threadDraftTitle.trim();
                            if (!title) {
                                setThreadDraftError("Thread name is required.");
                                return;
                            }
                            void ctx.hub
                                .createThread(workspaceId, channelId, threadDraft.messageId, title)
                                .then((t) => {
                                setThreads((prev) => new Map(prev).set(t.root_message_id, t));
                                setThreadDraft(null);
                                setOpenThread(t);
                            })
                                .catch((e) => {
                                setThreadDraftError(e instanceof Error ? e.message : String(e));
                            });
                        } }))] }), openThread && (_jsx(ThreadPanel, { workspaceId: workspaceId, channelId: channelId, thread: threads.get(openThread.root_message_id) ?? openThread, rootMessage: openThreadRoot, authorName: authorName, displayName: displayName, authorId: authorId, canResolve: true, onThreadUpdated: (t) => {
                    setThreads((prev) => new Map(prev).set(t.root_message_id, t));
                    setOpenThread(t);
                }, onClose: () => setOpenThread(null) }))] }));
}
// KTK-385 — compact inline affordance under a thread's root message,
// Discord-style: title, reply count, last-activity time. Clicking opens
// the side panel.
function ThreadAffordance({ thread, onOpen }) {
    return (_jsxs("button", { type: "button", onClick: onOpen, style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            margin: "2px 0 6px",
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid var(--border)",
            background: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
            maxWidth: 420,
        }, children: [_jsx(ThreadIcon, {}), _jsx("span", { style: {
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }, children: thread.title }), _jsxs("span", { style: { whiteSpace: "nowrap" }, children: [thread.reply_count, " ", thread.reply_count === 1 ? "reply" : "replies"] }), thread.last_reply_at && (_jsx("span", { style: { color: "var(--text-muted)", whiteSpace: "nowrap" }, children: new Date(thread.last_reply_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }))] }));
}
function ThreadIcon() {
    return (_jsxs("svg", { width: "13", height: "13", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.4", "aria-hidden": "true", children: [_jsx("path", { d: "M2 3h12M2 8h5M9.5 8H14M6 13h8" }), _jsx("path", { d: "M9.5 5.5V8" })] }));
}
// KTK-385 — minimal modal for naming a new thread. Dismisses via buttons
// and Escape only (house rule: no backdrop-click dismissal).
function ThreadTitleDialog({ title, error, onTitleChange, onCancel, onSubmit, }) {
    useEffect(() => {
        function keyHandler(e) {
            if (e.key === "Escape")
                onCancel();
        }
        document.addEventListener("keydown", keyHandler);
        return () => document.removeEventListener("keydown", keyHandler);
    }, [onCancel]);
    return (_jsx("div", { style: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "grid",
            placeItems: "center",
            zIndex: 120,
        }, children: _jsxs("div", { style: {
                width: 360,
                padding: 16,
                borderRadius: 8,
                background: "var(--bg-secondary)",
                border: "1px solid var(--border)",
                display: "flex",
                flexDirection: "column",
                gap: 10,
            }, children: [_jsx("div", { style: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }, children: "Create thread" }), _jsx("input", { type: "text", value: title, autoFocus: true, onChange: (e) => onTitleChange(e.target.value), onKeyDown: (e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            onSubmit();
                        }
                    }, placeholder: "Thread name", style: {
                        padding: "8px 10px",
                        background: "var(--bg-tertiary)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--text-primary)",
                        fontSize: 13,
                        fontFamily: "inherit",
                        outline: "none",
                    } }), error && _jsx("div", { style: { fontSize: 12, color: "var(--error)" }, children: error }), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8 }, children: [_jsx("button", { type: "button", onClick: onCancel, style: {
                                padding: "6px 12px",
                                fontSize: 13,
                                border: "none",
                                borderRadius: 6,
                                background: "transparent",
                                color: "var(--text-secondary)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                            }, children: "Cancel" }), _jsx("button", { type: "button", onClick: onSubmit, style: {
                                padding: "6px 12px",
                                fontSize: 13,
                                border: "none",
                                borderRadius: 6,
                                background: "var(--accent)",
                                color: "white",
                                cursor: "pointer",
                                fontFamily: "inherit",
                            }, children: "Create" })] })] }) }));
}
function SmileyIcon() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.3", children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("circle", { cx: "6", cy: "6.8", r: "0.6", fill: "currentColor", stroke: "none" }), _jsx("circle", { cx: "10", cy: "6.8", r: "0.6", fill: "currentColor", stroke: "none" }), _jsx("path", { d: "M5.5 9.6c.6.9 1.6 1.4 2.5 1.4s1.9-.5 2.5-1.4", strokeLinecap: "round" })] }));
}
function ReplyIcon() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M5 3L2 6l3 3" }), _jsx("path", { d: "M2 6h6.5a3 3 0 0 1 3 3v2" })] }));
}
function MoreIcon() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "currentColor", children: [_jsx("circle", { cx: "3", cy: "7", r: "1.2" }), _jsx("circle", { cx: "7", cy: "7", r: "1.2" }), _jsx("circle", { cx: "11", cy: "7", r: "1.2" })] }));
}
// KTK-273 — right-click message context menu. Lifts to ChannelView so
// the menu paints above everything (including the right-rail
// DecisionsSidebar) and dismisses on click-outside / Escape regardless
// of which message owns it.
function MessageContextMenu({ x, y, isPinned, canEdit, onReply, onCopy, onPin, onEdit, onCreateThread, onDismiss, }) {
    const ref = useRef(null);
    useEffect(() => {
        function mouseHandler(e) {
            if (ref.current && !ref.current.contains(e.target))
                onDismiss();
        }
        function keyHandler(e) {
            if (e.key === "Escape")
                onDismiss();
        }
        document.addEventListener("mousedown", mouseHandler);
        document.addEventListener("keydown", keyHandler);
        return () => {
            document.removeEventListener("mousedown", mouseHandler);
            document.removeEventListener("keydown", keyHandler);
        };
    }, [onDismiss]);
    return (_jsxs("div", { ref: ref, role: "menu", 
        // Clamp the menu inside the viewport so right-clicks near the right
        // edge don't hide it. 200/140 are conservative bounds; the menu's
        // intrinsic size is smaller.
        style: {
            position: "fixed",
            top: Math.min(y, window.innerHeight - 140),
            left: Math.min(x, window.innerWidth - 200),
            zIndex: 100,
            minWidth: 180,
            padding: 4,
            borderRadius: 6,
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border)",
            boxShadow: "0 6px 24px rgba(0, 0, 0, 0.45)",
        }, children: [_jsx("button", { type: "button", onClick: onReply, style: contextMenuItemStyle, children: "Reply" }), _jsx("button", { type: "button", onClick: onCopy, style: contextMenuItemStyle, children: "Copy text" }), onCreateThread && (_jsx("button", { type: "button", onClick: onCreateThread, style: contextMenuItemStyle, children: "Create thread" })), canEdit && (_jsx("button", { type: "button", onClick: onEdit, style: contextMenuItemStyle, children: "Edit message" })), _jsx("button", { type: "button", onClick: onPin, style: contextMenuItemStyle, children: isPinned ? "Unpin message" : "Pin message" })] }));
}
const contextMenuItemStyle = {
    width: "100%",
    padding: "6px 10px",
    fontSize: 13,
    textAlign: "left",
    background: "none",
    color: "var(--text-primary)",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
};
// KTK-273 — selection toolbar that floats next to a text selection inside
// a message. Visually mirrors the composer's selection toolbar so
// formatting and selection toolbars feel like the same vocabulary.
function MessageSelectionToolbar({ x, y, onCopy, onForward, onReply, }) {
    return (_jsxs("div", { role: "toolbar", 
        // mousedown shouldn't drop the selection — without preventDefault
        // the browser would clear the selection before our click handler
        // runs and the action would have no text to operate on.
        onMouseDown: (e) => e.preventDefault(), style: {
            position: "fixed",
            top: Math.max(8, y),
            left: Math.min(Math.max(8, x), window.innerWidth - 200),
            zIndex: 100,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            padding: "1px 6px",
            borderRadius: 8,
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.4)",
        }, children: [_jsx("button", { type: "button", onClick: onReply, style: toolbarBtnStyle, "aria-label": "Reply", children: "\u21A9 Reply" }), _jsx("button", { type: "button", onClick: onCopy, style: toolbarBtnStyle, "aria-label": "Copy selection", children: "Copy" }), _jsx("button", { type: "button", onClick: onForward, style: toolbarBtnStyle, "aria-label": "Forward", children: "Forward" })] }));
}
const MessageBubble = memo(function MessageBubble({ message, reactions, onReact, onPin, onDelete, onEdit, onReply, onContextMenu: onMenuRequest, isPinned, isEditing, onCancelEdit, mentionNames, favoriteEmojis, mruEmojis, fullEmojiPickerSet, selfName, ownerHandle, }) {
    const [editDraft, setEditDraft] = useState(message.content);
    // Reset the draft to the live message text whenever the bubble
    // (re-)enters edit mode — a Cancel followed by another Edit should
    // start from the saved content, not from the stale aborted draft.
    useEffect(() => {
        if (isEditing)
            setEditDraft(message.content);
    }, [isEditing, message.content]);
    // Reaction-picker popover — per-bubble, opens to the full emoji set
    // (workspace favorites then broader) when the smiley is clicked.
    const [reactionPickerOpen, setReactionPickerOpen] = useState(null);
    const isOwn = message.author_name === selfName;
    const isHuman = message.author_type === "human";
    const isSystem = message.author_type === "system" || message.author_type === "webhook";
    const authorColor = isHuman
        ? "var(--accent)"
        : isSystem
            ? "var(--text-muted)"
            : "var(--success)";
    const time = formatMessageStamp(message.created_at);
    const meta = (() => {
        if (!message.metadata)
            return null;
        if (typeof message.metadata === "string") {
            try {
                return JSON.parse(message.metadata);
            }
            catch {
                return null;
            }
        }
        return message.metadata;
    })();
    const singleImage = typeof meta?.image === "string" ? meta.image : null;
    const multipleImages = Array.isArray(meta?.images)
        ? meta.images.filter((v) => typeof v === "string")
        : null;
    const decisionCompletedId = meta?.kind === "decision_completed" && typeof meta.decision_id === "string"
        ? meta.decision_id
        : null;
    // 3 inline reaction slots — MRU first, padded from favorites if MRU is
    // shorter than 3, deduped so a favorite that's also in MRU doesn't
    // double up. (KTK-273 — Discord-style truncation.)
    const inlineReactionEmojis = (() => {
        const out = [];
        for (const e of mruEmojis) {
            if (!out.includes(e) && out.length < 3)
                out.push(e);
        }
        for (const e of favoriteEmojis) {
            if (!out.includes(e) && out.length < 3)
                out.push(e);
        }
        return out;
    })();
    // KTK-319 — only the message author (and only humans — agents don't
    // edit retroactively) gets the Edit affordance. Computed here because
    // the bubble already knows isOwn + isHuman; the menu gets `canEdit`
    // as a passive flag instead of having to look the message back up.
    const canEdit = isOwn && isHuman;
    const handleContextMenu = (e) => {
        // Don't hijack right-click inside the editing textarea or the action
        // strip itself — let the browser's native menu fire so users can
        // still get spell-check / paste menus while editing.
        if (isEditing)
            return;
        const target = e.target;
        if (target?.closest(".kmsg-actions"))
            return;
        e.preventDefault();
        onMenuRequest(message.id, message.content, message.author_name, isPinned, canEdit, e.clientX, e.clientY);
    };
    return (_jsxs("div", { className: "kmsg-row", onContextMenu: handleContextMenu, "data-message-id": message.id, "data-message-author": message.author_name, style: {
            display: "flex",
            gap: 12,
            padding: "8px 12px",
            margin: "0 -12px",
            position: "relative",
            borderRadius: 6,
        }, children: [_jsxs("div", { className: "kmsg-actions", 
                // Force the strip visible while a reaction popover is open — without
                // this the parent CSS hides it as soon as the cursor leaves the
                // message row to interact with the popover, taking the popover
                // (which is rendered as the strip's child) with it.
                style: {
                    position: "absolute",
                    right: 0,
                    top: -4,
                    zIndex: 10,
                    gap: 1,
                    padding: "2px 4px",
                    borderRadius: 6,
                    backgroundColor: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    ...(reactionPickerOpen ? { display: "flex" } : {}),
                }, children: [inlineReactionEmojis.map((emoji) => (_jsx("button", { type: "button", onClick: () => onReact(message.id, emoji), style: {
                            fontSize: 16,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "3px 5px",
                            borderRadius: 4,
                        }, children: emoji }, emoji))), _jsxs("div", { style: { position: "relative" }, children: [_jsx("button", { type: "button", onClick: () => setReactionPickerOpen((s) => (s === "more" ? null : "more")), "aria-label": "Add reaction", style: {
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "3px 5px",
                                    borderRadius: 4,
                                    color: reactionPickerOpen === "more"
                                        ? "var(--accent)"
                                        : "var(--text-muted)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }, children: _jsx(SmileyIcon, {}) }), reactionPickerOpen === "more" && (_jsx(ReactionPickerPopover, { emojis: fullEmojiPickerSet, onPick: (emoji) => {
                                    onReact(message.id, emoji);
                                    setReactionPickerOpen(null);
                                }, onDismiss: () => setReactionPickerOpen(null) }))] }), _jsx("button", { type: "button", onClick: () => onReply(message.id, message.content, message.author_name), "aria-label": "Reply", style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "3px 5px",
                            borderRadius: 4,
                            color: "var(--text-muted)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }, children: _jsx(ReplyIcon, {}) }), _jsx("button", { type: "button", onClick: (e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            onMenuRequest(message.id, message.content, message.author_name, isPinned, canEdit, r.left, r.bottom + 4);
                        }, "aria-label": "More options", style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "3px 5px",
                            borderRadius: 4,
                            color: "var(--text-muted)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }, children: _jsx(MoreIcon, {}) })] }), _jsx("div", { style: {
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    marginTop: 2,
                    backgroundColor: isHuman
                        ? "var(--accent)"
                        : isSystem
                            ? "var(--bg-hover)"
                            : "var(--success)",
                    color: "white",
                }, children: message.author_name.charAt(0).toUpperCase() }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }, children: [_jsx("span", { style: { fontWeight: 600, fontSize: 14, color: authorColor }, children: message.author_name }), _jsx("span", { style: { fontSize: 11, color: "var(--text-muted)" }, children: time }), message.edited_at && (_jsx("span", { style: { fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }, title: `Edited ${new Date(message.edited_at).toLocaleString()}`, children: "(edited)" })), isPinned && (_jsx("span", { style: { fontSize: 11, color: "var(--accent)" }, children: "\uD83D\uDCCC pinned" }))] }), _jsxs("div", { style: { fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5 }, children: [isEditing ? (_jsxs("form", { onSubmit: async (e) => {
                                    e.preventDefault();
                                    const trimmed = editDraft.trim();
                                    if (trimmed && trimmed !== message.content) {
                                        await onEdit(message.id, trimmed);
                                    }
                                    else {
                                        // Empty or unchanged save — just back out. handleEditMessage
                                        // would otherwise be the one to clear editingMessageId.
                                        onCancelEdit();
                                    }
                                }, style: { display: "flex", flexDirection: "column", gap: 6 }, children: [_jsx("textarea", { value: editDraft, onChange: (e) => setEditDraft(e.target.value), onKeyDown: (e) => {
                                            if (e.key === "Escape")
                                                onCancelEdit();
                                        }, autoFocus: true, rows: 3, style: {
                                            padding: "6px 10px",
                                            background: "var(--bg-tertiary)",
                                            border: "1px solid var(--border)",
                                            borderRadius: 6,
                                            color: "var(--text-primary)",
                                            fontSize: 13,
                                            fontFamily: "inherit",
                                            resize: "vertical",
                                        } }), _jsxs("div", { style: { display: "flex", gap: 6, justifyContent: "flex-end" }, children: [_jsx("button", { type: "button", onClick: onCancelEdit, style: {
                                                    padding: "4px 10px",
                                                    background: "transparent",
                                                    color: "var(--text-secondary)",
                                                    border: "none",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                }, children: "Cancel" }), _jsx("button", { type: "submit", style: {
                                                    padding: "4px 10px",
                                                    background: "var(--accent)",
                                                    color: "white",
                                                    border: "none",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                }, children: "Save" })] })] })) : decisionCompletedId ? (_jsx(DecisionCompletedCard, { decisionId: decisionCompletedId, content: message.content })) : (_jsx(MentionAwareContent, { content: message.content, mentionNames: mentionNames, ownerName: ownerHandle })), !isEditing && singleImage && (_jsx("div", { style: { marginTop: 6 }, children: _jsx(AuthedImage, { src: singleImage, alt: "attachment", placeholderHeight: 200, style: {
                                        maxWidth: "100%",
                                        maxHeight: 400,
                                        borderRadius: 6,
                                        display: "block",
                                    } }) })), !isEditing && multipleImages && (_jsx("div", { style: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }, children: multipleImages.map((url, i) => (_jsx(AuthedImage, { src: url, alt: `attachment ${i + 1}`, placeholderHeight: 120, style: {
                                        maxWidth: 220,
                                        maxHeight: 160,
                                        borderRadius: 6,
                                        display: "block",
                                    } }, i))) }))] }), reactions.length > 0 && (_jsx("div", { style: { display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }, children: reactions.map((r) => {
                            const mine = r.authors.includes(selfName);
                            return (_jsx(ReactionBadge, { emoji: r.emoji, count: r.count, authors: r.authors, mine: mine, selfName: selfName, onClick: () => onReact(message.id, r.emoji) }, r.emoji));
                        }) }))] })] }));
});
// Compact reaction picker — used by both the workspace-favorites popover
// (the ⭐ button) and the additional-emoji popover (the ＋ button).
// Click-outside dismissal is handled via a document mousedown listener.
function ReactionPickerPopover({ emojis, onPick, onDismiss, }) {
    const ref = useRef(null);
    useEffect(() => {
        function handler(e) {
            if (ref.current && !ref.current.contains(e.target))
                onDismiss();
        }
        function escHandler(e) {
            if (e.key === "Escape")
                onDismiss();
        }
        document.addEventListener("mousedown", handler);
        document.addEventListener("keydown", escHandler);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("keydown", escHandler);
        };
    }, [onDismiss]);
    return (_jsx("div", { ref: ref, role: "menu", style: {
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            zIndex: 20,
            display: "grid",
            gridTemplateColumns: "repeat(8, 28px)",
            gap: 2,
            padding: 6,
            borderRadius: 8,
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
            maxWidth: 256,
        }, children: emojis.map((e) => (_jsx("button", { type: "button", onClick: () => onPick(e), style: {
                width: 28,
                height: 28,
                fontSize: 18,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                borderRadius: 4,
            }, children: e }, e))) }));
}
function MentionAwareContent({ content, mentionNames, ownerName, }) {
    if (mentionNames.length === 0)
        return _jsx(MarkdownContent, { content: content });
    const escaped = mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(`@(${escaped.join("|")})\\b`, "g");
    if (!regex.test(content))
        return _jsx(MarkdownContent, { content: content });
    return (_jsx(MarkdownContent, { content: content, mentionRegex: regex, ownerName: ownerName }));
}
// Walk the rendered markdown tree and wrap any `@name` text runs in a styled
// span. We can't use react-markdown's `text` override reliably because
// remark-gfm produces text nodes split across autolink / mention boundaries,
// so we process the children of block components instead.
function highlightMentions(children, mentionRegex, ownerName) {
    let key = 0;
    const walk = (node) => {
        if (typeof node === "string") {
            mentionRegex.lastIndex = 0;
            if (!mentionRegex.test(node))
                return node;
            mentionRegex.lastIndex = 0;
            const parts = [];
            let lastIdx = 0;
            let m;
            while ((m = mentionRegex.exec(node)) !== null) {
                if (m.index > lastIdx)
                    parts.push(node.slice(lastIdx, m.index));
                const isOwner = m[1] === ownerName;
                parts.push(_jsxs("span", { style: {
                        backgroundColor: isOwner ? OWNER_PILL_BG : AGENT_PILL_BG,
                        color: isOwner ? OWNER_PILL_COLOR : AGENT_PILL_COLOR,
                        padding: "1px 4px",
                        borderRadius: 3,
                        fontWeight: 600,
                    }, children: ["@", m[1]] }, `mention-${key++}`));
                lastIdx = m.index + m[0].length;
            }
            if (lastIdx < node.length)
                parts.push(node.slice(lastIdx));
            return _jsx(_Fragment, { children: parts });
        }
        if (Array.isArray(node))
            return node.map(walk);
        if (node && typeof node === "object" && "props" in node) {
            const el = node;
            const ch = el.props?.children;
            if (ch === undefined || ch === null)
                return el;
            return {
                ...el,
                props: { ...el.props, children: walk(ch) },
            };
        }
        return node;
    };
    return walk(children);
}
// Exported for ThreadPanel (KTK-385) — thread replies render with the same
// markdown pipeline as the parent feed.
export function MarkdownContent({ content, mentionRegex, ownerName, }) {
    const decorate = (children) => mentionRegex ? highlightMentions(children, mentionRegex, ownerName ?? "") : children;
    return (_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
            // KTK-272 — fenced code blocks (``` … ```) come through as
            // <pre><code>…</code></pre>. Override `pre` so we always render the
            // shared MessageCodeBlock for blocks, regardless of whether a
            // language suffix was given. The `code` override below stays
            // inline-only and never has to disambiguate block-vs-inline.
            pre({ node }) {
                const codeNode = node?.children?.find((c) => c.type === "element" &&
                    c.tagName === "code");
                const classNames = Array.isArray(codeNode?.properties?.className)
                    ? codeNode.properties.className
                    : typeof codeNode?.properties?.className === "string"
                        ? codeNode.properties.className.split(/\s+/)
                        : [];
                const langClass = classNames.find((c) => c.startsWith("language-"));
                const lang = langClass ? langClass.slice("language-".length) : undefined;
                const text = (codeNode?.children ?? [])
                    .map((n) => (n.type === "text" ? n.value ?? "" : ""))
                    .join("");
                return _jsx(MessageCodeBlock, { lang: lang, children: text.replace(/\n$/, "") });
            },
            code({ className, children }) {
                // Inline code only — fenced blocks are handled by the `pre`
                // override above, which short-circuits this branch entirely
                // (it returns a custom element ignoring our `code` output).
                return (_jsx("code", { className: className, style: {
                        padding: "1px 5px",
                        borderRadius: 3,
                        background: "var(--bg-tertiary)",
                        fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                        fontSize: 12,
                    }, children: children }));
            },
            a({ children, ...props }) {
                return (_jsx("a", { ...props, style: { color: "var(--accent)" }, target: "_blank", rel: "noreferrer", children: children }));
            },
            p({ children }) {
                return _jsx("p", { style: { margin: "4px 0" }, children: decorate(children) });
            },
            strong({ children }) {
                return _jsx("strong", { style: { fontWeight: 600 }, children: decorate(children) });
            },
            em({ children }) {
                return _jsx("em", { style: { fontStyle: "italic" }, children: decorate(children) });
            },
            ul({ children }) {
                return (_jsx("ul", { style: { margin: "4px 0", paddingLeft: 24, listStyleType: "disc" }, children: children }));
            },
            ol({ children }) {
                return (_jsx("ol", { style: { margin: "4px 0", paddingLeft: 24, listStyleType: "decimal" }, children: children }));
            },
            li({ children }) {
                return _jsx("li", { style: { listStylePosition: "outside" }, children: decorate(children) });
            },
            h1({ children }) {
                return _jsx("h1", { style: { fontSize: 18, margin: "10px 0 6px", fontWeight: 600 }, children: decorate(children) });
            },
            h2({ children }) {
                return _jsx("h2", { style: { fontSize: 16, margin: "10px 0 6px", fontWeight: 600 }, children: decorate(children) });
            },
            h3({ children }) {
                return _jsx("h3", { style: { fontSize: 14, margin: "8px 0 4px", fontWeight: 600 }, children: decorate(children) });
            },
            blockquote({ children }) {
                return (_jsx("blockquote", { style: {
                        margin: "4px 0",
                        paddingLeft: 12,
                        borderLeft: "3px solid var(--border)",
                        color: "var(--text-secondary)",
                    }, children: decorate(children) }));
            },
            hr() {
                return (_jsx("hr", { style: {
                        border: "none",
                        borderTop: "1px solid var(--border)",
                        margin: "10px 0",
                    } }));
            },
            table({ children }) {
                return (_jsx("table", { style: {
                        borderCollapse: "collapse",
                        margin: "6px 0",
                        fontSize: 12,
                    }, children: children }));
            },
            th({ children }) {
                return (_jsx("th", { style: {
                        border: "1px solid var(--border)",
                        padding: "4px 8px",
                        background: "var(--bg-tertiary)",
                        textAlign: "left",
                    }, children: children }));
            },
            td({ children }) {
                return (_jsx("td", { style: { border: "1px solid var(--border)", padding: "4px 8px" }, children: children }));
            },
        }, children: content }));
}
// Short-name lookup for the reaction tooltip. Slack-style, falls back to
// "reaction" for unmapped glyphs so the tooltip still makes sense.
const EMOJI_SHORT_NAMES = {
    "👍": "thumbsup",
    "👎": "thumbsdown",
    "❤️": "heart",
    "🎉": "tada",
    "👀": "eyes",
    "🔥": "fire",
    "✅": "white_check_mark",
    "🙏": "pray",
    "🤦‍♀️": "facepalm",
    "🤦": "facepalm",
    "🤦‍♂️": "facepalm",
    "😂": "joy",
    "😅": "sweat_smile",
    "😎": "sunglasses",
    "💯": "100",
    "🚀": "rocket",
    "👏": "clap",
    "🙌": "raised_hands",
    "💪": "muscle",
    "🤔": "thinking",
    "😢": "cry",
    "😭": "sob",
    "😡": "rage",
    "❌": "x",
    "🆗": "ok",
    "📌": "pushpin",
    "🐛": "bug",
    "✨": "sparkles",
    "🤷": "shrug",
    "🤷‍♀️": "shrug",
    "🤷‍♂️": "shrug",
};
function emojiShortName(emoji) {
    return EMOJI_SHORT_NAMES[emoji] ?? "reaction";
}
function ReactionBadge({ emoji, count, authors, mine, selfName, onClick, }) {
    const [hovered, setHovered] = useState(false);
    const shortName = emojiShortName(emoji);
    return (_jsxs("span", { style: { position: "relative", display: "inline-flex" }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("button", { type: "button", onClick: onClick, style: {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 11,
                    cursor: "pointer",
                    backgroundColor: mine ? "rgba(124,92,252,0.2)" : "var(--bg-tertiary)",
                    border: `1px solid ${mine ? "var(--accent)" : "var(--border)"}`,
                    color: "var(--text-primary)",
                    fontFamily: "inherit",
                }, children: [_jsx("span", { children: emoji }), _jsx("span", { children: count })] }), hovered && (_jsxs("div", { role: "tooltip", style: {
                    position: "absolute",
                    bottom: "calc(100% + 4px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    maxWidth: 220,
                    padding: "4px 8px 5px",
                    borderRadius: 6,
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                    zIndex: 30,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    pointerEvents: "none",
                }, children: [_jsx("span", { style: { fontSize: 18, lineHeight: 1 }, children: emoji }), _jsxs("span", { style: { fontSize: 10, color: "var(--text-muted)", lineHeight: 1.2 }, children: [":", shortName, ":"] }), _jsx("span", { style: {
                            fontSize: 11,
                            color: "var(--text-primary)",
                            textAlign: "center",
                            maxWidth: 200,
                            lineHeight: 1.3,
                        }, children: _jsx(ReactionAuthors, { authors: authors, selfName: selfName }) })] }))] }));
}
function dedupeReactionAuthors(authors, selfName) {
    // De-dup defensively in case the backend still has stray duplicate rows.
    const seen = new Set();
    const unique = [];
    for (const name of authors) {
        const key = name === selfName ? "you" : name;
        if (seen.has(key))
            continue;
        seen.add(key);
        unique.push(name === selfName ? "You" : name);
    }
    return unique;
}
// Renders a reactor list as inline tokens — names sit in
// display:inline-block; white-space:nowrap chips so the browser only ever
// wraps between names, never inside a hyphenated handle like "team-lead".
function ReactionAuthors({ authors, selfName, }) {
    const names = dedupeReactionAuthors(authors, selfName);
    if (names.length === 0)
        return null;
    return (_jsx("span", { style: { display: "inline" }, children: names.map((n, i) => {
            let separator = "";
            if (i > 0) {
                if (names.length === 2)
                    separator = " and ";
                else if (i === names.length - 1)
                    separator = ", and ";
                else
                    separator = ", ";
            }
            return (_jsxs("span", { children: [separator, _jsx("span", { style: { display: "inline-block", whiteSpace: "nowrap" }, children: n })] }, `${n}-${i}`));
        }) }));
}
function AuthedImage({ src, alt, placeholderHeight, style, }) {
    const ctx = useDataContext();
    const [resolvedSrc, setResolvedSrc] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setResolvedSrc(null);
        setLoaded(false);
        setFailed(false);
        ctx.hub
            .authedImageUrl(src)
            .then((u) => {
            if (!cancelled)
                setResolvedSrc(u);
        })
            .catch(() => {
            if (!cancelled)
                setFailed(true);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, src]);
    if (failed) {
        return (_jsx("div", { role: "img", "aria-label": alt, style: {
                ...style,
                minHeight: placeholderHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--bg-tertiary)",
                color: "var(--text-muted)",
                fontSize: 11,
            }, children: "Couldn't load image" }));
    }
    return (_jsx("div", { style: {
            ...style,
            minHeight: loaded ? undefined : placeholderHeight,
            background: loaded ? "transparent" : "var(--bg-tertiary)",
        }, children: resolvedSrc && (_jsx("img", { src: resolvedSrc, alt: alt, onLoad: () => setLoaded(true), onError: () => setFailed(true), style: { ...style, opacity: loaded ? 1 : 0, transition: "opacity 0.15s" } })) }));
}
/**
 * Parse D1's default `datetime('now')` string as UTC so the browser's
 * toLocaleTimeString renders it in the viewer's local zone. Without this,
 * "2026-04-20 13:42:00" is treated as local time on some browsers and as
 * UTC on others — inconsistent and usually wrong for us since the server
 * emits UTC. Leaves ISO/Z-stamped strings alone.
 */
/**
 * KTK-308 — per-message timestamp stamp. Today's messages show just the
 * time (clean, low-noise for the active conversation); messages from
 * any other day prefix the numeric short date, matching Discord:
 *   Today:    "12:34 PM"
 *   Other:    "4/20/2026 12:34 PM"
 */
function formatMessageStamp(raw) {
    const d = parseServerTimestamp(raw);
    if (Number.isNaN(d.getTime()))
        return "";
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (today.getTime() === that.getTime())
        return time;
    return `${d.toLocaleDateString()} ${time}`;
}
/**
 * KTK-308 — local-calendar-day key for a server timestamp. Year-month-day
 * in the viewer's local timezone, used to detect when consecutive
 * messages cross a day boundary and a date divider should be drawn.
 */
function localDayKey(raw) {
    const d = parseServerTimestamp(raw);
    if (Number.isNaN(d.getTime()))
        return "";
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
/**
 * KTK-308 — Discord-style day divider drawn above the first message
 * of each new calendar day. "Today" / "Yesterday" for the last two
 * days, weekday+date for the rest of the current year, full date for
 * older messages.
 */
function DateDivider({ iso }) {
    const d = parseServerTimestamp(iso);
    const label = Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    return (_jsxs("div", { role: "separator", "aria-label": label, style: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "16px 24px 8px",
            color: "var(--ink-3)",
        }, children: [_jsx("span", { style: { flex: 1, height: 1, background: "var(--hairline)" } }), _jsx("span", { style: {
                    fontFamily: "var(--mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    padding: "2px 10px",
                    background: "var(--paper-2)",
                    border: "1px solid var(--hairline)",
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                }, children: label }), _jsx("span", { style: { flex: 1, height: 1, background: "var(--hairline)" } })] }));
}
function parseServerTimestamp(raw) {
    if (!raw)
        return new Date(NaN);
    // Already has an explicit offset (trailing Z, ±HH:MM).
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw))
        return new Date(raw);
    // SQLite format: space separator, no tz. Treat as UTC.
    const iso = raw.includes("T") ? `${raw}Z` : `${raw.replace(" ", "T")}Z`;
    return new Date(iso);
}
function relayFrameToMessage(msg) {
    // Electron's sendHumanMessage + channel-hub emits flat fields. Some frames
    // carry metadata as a parsed object, others (from D1 round-trips) as a JSON
    // string; normalize both shapes.
    const nested = msg.message;
    if (nested && typeof nested.id === "string")
        return nested;
    if (typeof msg.message_id !== "string")
        return null;
    if (typeof msg.channel_id !== "string")
        return null;
    if (typeof msg.content !== "string")
        return null;
    let metadata = null;
    const rawMeta = msg.metadata;
    if (rawMeta && typeof rawMeta === "object") {
        metadata = rawMeta;
    }
    else if (typeof rawMeta === "string") {
        try {
            metadata = JSON.parse(rawMeta);
        }
        catch {
            metadata = null;
        }
    }
    const authorType = (msg.author_type ?? "agent");
    const rawTimestamp = msg.timestamp;
    const timestamp = typeof rawTimestamp === "string" ? rawTimestamp : new Date().toISOString();
    return {
        id: msg.message_id,
        channel_id: msg.channel_id,
        author_type: authorType,
        author_id: typeof msg.author_id === "string" ? msg.author_id : null,
        author_name: typeof msg.author_name === "string" ? msg.author_name : "Unknown",
        content: msg.content,
        metadata,
        created_at: timestamp,
    };
}
function ChannelRoster({ ownerName, agents, }) {
    const STALE_MS = 30_000;
    const now = Date.now();
    return (_jsxs("div", { children: [_jsx("div", { style: {
                    padding: "2px 12px 6px",
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: "var(--text-muted)",
                }, children: "Channel members" }), _jsx(RosterRow, { name: ownerName, dotColor: OWNER_PILL_COLOR, nameColor: OWNER_PILL_COLOR, suffix: "(you)" }), agents.length === 0 ? (_jsx("div", { style: {
                    padding: "6px 12px",
                    color: "var(--text-muted)",
                    fontStyle: "italic",
                }, children: "No agents subscribed." })) : (agents.map((a) => {
                const seen = a.last_seen_at
                    ? Date.parse(a.last_seen_at)
                    : NaN;
                const fresh = Number.isFinite(seen) && now - seen < STALE_MS;
                const dotColor = a.status === "error"
                    ? "var(--error)"
                    : a.status === "running" && fresh
                        ? "var(--success)"
                        : a.status === "running"
                            ? "var(--text-muted)"
                            : "var(--text-muted)";
                return (_jsx(RosterRow, { name: a.name, dotColor: dotColor, nameColor: "var(--text-primary)" }, a.id));
            }))] }));
}
function RosterRow({ name, dotColor, nameColor, suffix, }) {
    return (_jsxs("div", { style: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 12px",
        }, children: [_jsx("span", { style: {
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                    flexShrink: 0,
                } }), _jsxs("span", { style: { color: nameColor, fontWeight: 600 }, children: ["@", name] }), suffix && (_jsx("span", { style: { color: "var(--text-muted)", fontSize: 11 }, children: suffix }))] }));
}
//# sourceMappingURL=ChannelView.js.map